/**
 * Spline entity → `RenderableEntity` adapter.
 *
 * # CLAUDE.md priority #1
 *
 * Cubic Bezier ONLY. Tangent vectors at first / last control points
 * (DXF group codes 12 / 13) are preserved EXPLICITLY on the
 * `RenderableEntity.geometry.tangentVectors` field — never quadratic,
 * never approximated to a straight polyline. Renderer side uses
 * `buildSplineBeziers` from `@dxf-ai-editor/drawing-model` which
 * already encodes the cubic-only semantics.
 */

import type { EntityPreview } from "@dxf-ai-editor/contracts";

import { readNumber } from "../../scene/renderHelpers";
import type { RenderableEntity, Vec3 } from "../types";

import { buildRenderableShell, type AdapterContext } from "./adapterContext";
import { buildSplineSceneGeometry } from "./geom/splineGeometry";

/** Adapter for DXF SPLINE / `Spline` entity type. */
export function splineToRenderable(
  entity: EntityPreview,
  ctx: AdapterContext,
): RenderableEntity {
  // Control points are absolute world positions; the shared builder applies
  // `applyOffset` (so we pass the RAW pre-offset points here).
  const controlPointsRaw = parsePointPairs(
    entity.properties["Control points"] ?? "",
  );
  const knots = parseNumberList(entity.properties.Knots ?? "");
  const degree = Math.max(
    1,
    Math.round(readNumber(entity.properties, "Degree", 3)),
  );

  // Tangent vectors are stored as raw deltas — they are *not* world
  // points, so the builder does NOT pass them through `applyOffset`. They
  // live in the renderable as the same delta the DXF stored.
  const startTangent = readOptionalVec3(
    entity.properties,
    "Start tangent X",
    "Start tangent Y",
    "Start tangent Z",
  );
  const endTangent = readOptionalVec3(
    entity.properties,
    "End tangent X",
    "End tangent Y",
    "End tangent Z",
  );
  const tangentVectors =
    startTangent !== null && endTangent !== null
      ? { start: startTangent, end: endTangent }
      : undefined;

  const { geometry, bounds } = buildSplineSceneGeometry(
    controlPointsRaw,
    knots,
    degree,
    tangentVectors,
  );

  return buildRenderableShell(entity, ctx, geometry, bounds);
}

/** Parse `"x1,y1; x2,y2; …"` into a Vec3 array. */
function parsePointPairs(raw: string): Vec3[] {
  if (!raw) return [];
  return raw
    .split(";")
    .map((s) => s.trim())
    .filter((s) => s.length > 0)
    .map((s): Vec3 => {
      const parts = s.split(",");
      const x = Number(parts[0] ?? 0);
      const y = Number(parts[1] ?? 0);
      const z = parts[2] !== undefined ? Number(parts[2]) : 0;
      return { x, y, z };
    })
    .filter(
      (p): p is Vec3 =>
        Number.isFinite(p.x) && Number.isFinite(p.y) && Number.isFinite(p.z),
    );
}

/** Parse `"a; b; c; …"` into a number array. */
function parseNumberList(raw: string): number[] {
  if (!raw) return [];
  return raw
    .split(/[;,\s]+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0)
    .map((s) => Number(s))
    .filter((n) => Number.isFinite(n));
}

/** Read three optional numeric properties; returns null when any is missing. */
function readOptionalVec3(
  properties: Record<string, string>,
  xKey: string,
  yKey: string,
  zKey: string,
): Vec3 | null {
  const xRaw = properties[xKey];
  const yRaw = properties[yKey];
  if (xRaw === undefined || yRaw === undefined) return null;
  const x = Number(xRaw);
  const y = Number(yRaw);
  const zRaw = properties[zKey];
  const z = zRaw !== undefined ? Number(zRaw) : 0;
  if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) {
    return null;
  }
  return { x, y, z };
}
